import { inject, Injectable } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionOperationRequest,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { AdminOrderCollectionItemResponse } from '../responses/admin-order-collection-item.response';
import { AdminOrderDetailsResponse } from '../responses/admin-order-details.response';
import { AdminCreateAdditionalShipmentRequest } from '../requests/admin-create-additional-shipment.request';
import { AdminOrderShippingMethodCollectionItemResponse } from '../responses/admin-order-shipping-method-collection-item.response';
import { AdminOrderShippingAddressUpdateRequest } from '../requests/admin-order-shipping-address-update.request';
import { AdminOrderShipmentResponse } from '../responses/admin-order-shipment.response';
import { AdminOrderMarkAsCompleteResponse } from '../responses/admin-order-mark-as-complete.response';
import { AdminOrderRefundDataResponse } from '../responses/admin-order-refund-data.response';
import { AdminOrderPartialRefundRequest } from '../requests/admin-order-partial-refund.request';

@Injectable({
  providedIn: 'root',
})
export class AdminOrderApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    request: CollectionRequest;
    cacheable?: boolean;
    skipByLoading?: boolean;
  }): Observable<CollectionResponse<AdminOrderCollectionItemResponse>> {
    const path = 'admin/order';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminOrderCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(options: {
    id: Guid;
  }): Observable<AdminOrderDetailsResponse> {
    const { id } = options;
    const path = `admin/order/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(AdminOrderDetailsResponse)),
      );
  }

  public fetchAvailableShippingMethodCollection(params: {
    cacheable?: boolean;
    countryId?: Nillable<Guid>;
    orderId: Guid;
    request: CollectionRequest;
    shipmentId: Guid;
    skipByLoading: boolean;
  }): Observable<
    CollectionResponse<AdminOrderShippingMethodCollectionItemResponse>
  > {
    const path = `admin/order/${HttpParams.OrderId}/shipment/${HttpParams.ShipmentId}/shipping/method`;
    let queryParams = this.provider.httpHelper.collectionRequestToQueryParams(
      params.request,
    );

    if (!isNil(params.countryId)) {
      queryParams = { ...queryParams, countryId: params.countryId };
    }

    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams(queryParams)
        .params({
          [HttpParams.OrderId]: params.orderId,
          [HttpParams.ShipmentId]: params.shipmentId,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(params?.cacheable, false),
      )
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(params?.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminOrderShippingMethodCollectionItemResponse,
          ),
        ),
      );
  }

  public updateShippingAddress(param: {
    orderId: Guid;
    shipmentId: Guid;
    request: AdminOrderShippingAddressUpdateRequest;
  }): Observable<void> {
    const { orderId, request, shipmentId } = param;
    const path = `admin/order/${HttpParams.OrderId}/shipment/${HttpParams.ShipmentId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.OrderId]: orderId,
      [HttpParams.ShipmentId]: shipmentId,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminOrderShippingAddressUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public fetchShippingAddressDetails(params: {
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<AdminOrderShipmentResponse> {
    const { orderId, shipmentId } = params;
    const path = `admin/order/${HttpParams.OrderId}/shipment/${HttpParams.ShipmentId}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.OrderId]: orderId,
      [HttpParams.ShipmentId]: shipmentId,
    });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminOrderShipmentResponse),
        ),
      );
  }

  public fullRefundOrderById(id: Guid): Observable<void> {
    const path = `admin/order/${HttpParams.Id}/refund/full`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const body = this.provider.httpHelper.emptyBody();
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public fetchRefundDataById(options: {
    id: Guid;
  }): Observable<AdminOrderRefundDataResponse> {
    const { id } = options;
    const path = `admin/order/${HttpParams.Id}/refund`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminOrderRefundDataResponse),
        ),
      );
  }

  public partialRefundOrderById(
    id: Guid,
    request: AdminOrderPartialRefundRequest,
  ): Observable<void> {
    const path = `admin/order/${HttpParams.Id}/refund`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminOrderPartialRefundRequest,
    );
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public cancelById(id: Guid): Observable<void> {
    const path = `admin/order/${HttpParams.Id}/cancel`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const body = this.provider.httpHelper.emptyBody();
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public markAsPaidById(id: Guid): Observable<void> {
    const path = `admin/order/${HttpParams.Id}/mark-as-paid`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.Id]: id,
        })
        .build(),
    );
    const body = this.provider.httpHelper.emptyBody();
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public generateAndSend(params: {
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<void> {
    const { orderId, shipmentId } = params;
    const path = `admin/order/${HttpParams.OrderId}/shipment/${HttpParams.ShipmentId}/send`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.OrderId]: orderId,
          [HttpParams.ShipmentId]: shipmentId,
        })
        .build(),
    );
    const body = this.provider.httpHelper.emptyBody();
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public removeShipmentFromOrder(params: {
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<void> {
    const { orderId, shipmentId } = params;
    const path = `admin/order/${HttpParams.OrderId}/shipment/${HttpParams.ShipmentId}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .params({
          [HttpParams.OrderId]: orderId,
          [HttpParams.ShipmentId]: shipmentId,
        })
        .build(),
    );

    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  public downloadLabels(request: CollectionOperationRequest): Observable<Blob> {
    const path = `admin/order/bulk/shipment/label/download`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);

    const body = this.provider.httpHelper.requestToBody(
      request,
      CollectionOperationRequest,
    );

    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, {
      context,
      responseType: 'blob',
    });
  }

  public markAsCompleteMultiple(
    request: CollectionOperationRequest,
  ): Observable<AdminOrderMarkAsCompleteResponse> {
    const path = `admin/order/bulk/complete`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);

    const body = this.provider.httpHelper.requestToBody(
      request,
      CollectionOperationRequest,
    );

    const context = this.provider.httpContextBuilder.build();

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminOrderMarkAsCompleteResponse,
          ),
        ),
      );
  }

  public markAsCompleteById(id: Guid): Observable<Blob> {
    const path = `admin/order/${id}/complete`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const body = this.provider.httpHelper.emptyBody();
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.post(url, body, {
      context,
      responseType: 'blob',
    });
  }

  public downloadLabelFor(param: {
    orderId: string;
    shipmentId: string;
  }): Observable<Blob> {
    const path = `admin/order/${HttpParams.OrderId}/shipment/${HttpParams.ShipmentId}/download`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.OrderId]: param.orderId,
      [HttpParams.ShipmentId]: param.shipmentId,
    });

    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.get(url, {
      context,
      responseType: 'blob',
    });
  }

  /* prettier-ignore */
  public createAdditionalShipment(params: {
    orderId: string;
    request: AdminCreateAdditionalShipmentRequest;
  }): Observable<void> {
    const path = `admin/order/${HttpParams.OrderId}/shipment`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.OrderId]: params.orderId });
    const body = this.provider.httpHelper.requestToBody(params.request, AdminCreateAdditionalShipmentRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { context })
      .pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
